import { forwardRef } from 'react';
import clinicLogo from '../../assets/logo.png';
import { formatAge, formatDate, titleCase } from '../../utils/format';

/**
 * The printed prescription. Rendered at real paper size so what is on screen
 * is exactly what comes out of the printer.
 */
const PrescriptionSheet = forwardRef(function PrescriptionSheet({ data }, ref) {
  const { clinic, patient, visit, consultation, doctor, visitNumber } = data;
  const v = visit.vitals || {};

  // The signed-in doctor wins; clinic settings are the fallback.
  const doctorName = doctor?.name || clinic.doctorName;
  const doctorQualification = doctor?.qualification || clinic.qualification;
  const doctorSpecialization = doctor?.specialization || '';
  const doctorRegNo = doctor?.registrationNo || clinic.registrationNo;

  const qualifications = [doctorQualification, doctorSpecialization].filter(Boolean).join(', ');

  // Who the patient is, then - under a rule - what was measured today.
  // Anything not measured drops out instead of printing an empty label.
  const leftColumn = [
    { k: 'Name', v: patient.name },
    { k: 'Age', v: formatAge(patient) },
    v.weight && { k: 'Weight', v: `${v.weight} kg` },
  ].filter(Boolean);

  const rightColumn = [
    { k: 'Date', v: formatDate(visit.visitDate) },
    { k: 'Gender', v: titleCase(patient.gender) },
    // Which time this is that the patient has come in.
    visitNumber > 0 && { k: 'Visit', v: String(visitNumber) },
  ].filter(Boolean);


  const pair = (d) => (
    <div className="rx-pair" key={d.k}>
      <span className="rx-field-label">{d.k}:</span>
      <span className="rx-field-value">{d.v}</span>
    </div>
  );

  return (
    <div className={`rx-sheet ${clinic.paperSize === 'A5' ? 'a5' : ''}`} ref={ref}>
      {clinic.printHeader ? (
        <div className="rx-header">
          {/* The clinic's mark on the left, everything about the doctor and
              where to find him on the right. */}
          <div className="rx-header-logo">
            <img src={clinic.logoUrl || clinicLogo} alt={clinic.clinicName} />
          </div>

          <div className="rx-header-doctor">
            <div className="rx-doctor-name">{doctorName}</div>
            {qualifications && <div className="rx-doctor-quals">{qualifications}</div>}

            <div className="rx-small">
              {clinic.address && <div className="rx-address">{clinic.address}</div>}
              {clinic.timings && <div className="rx-timing">{clinic.timings}</div>}
              {clinic.sundayTimings && <div className="rx-timing">Sun | {clinic.sundayTimings}</div>}
              {doctorRegNo && <div>Reg. No: {doctorRegNo}</div>}
              {clinic.phone && <div className="rx-phone">Phone: {clinic.phone}</div>}
            </div>
          </div>
        </div>
      ) : (
        // Pre-printed letterhead: leave exactly the blank space that was asked
        // for. Zero means none at all - the sheet starts straight away.
        Number(clinic.headerTopSpaceMm) > 0 && (
          <div style={{ height: `${Number(clinic.headerTopSpaceMm)}mm` }} />
        )
      )}

      <div className="rx-patient-strip">
        <div className="rx-idcols">
          <div className="rx-idcol">{leftColumn.map(pair)}</div>
          <div className="rx-idcol">{rightColumn.map(pair)}</div>
        </div>

      </div>

      {patient.allergies?.length > 0 && (
        <div className="rx-allergy">
          ALLERGIC TO: {patient.allergies.join(', ')}
        </div>
      )}

      {/* Complaints, examination and diagnosis stay on the record but are not
          printed - the patient's copy carries the treatment only. */}

      {consultation.medicines?.length > 0 && (
        <div className="rx-section">
          {/* <div className="rx-symbol">Rx</div> */}
          <table className="rx-med-table">
            <thead>
              <tr>
                <th style={{ width: '6%' }}>Sr.</th>
                <th style={{ width: '48%' }}>Medicine</th>
                <th style={{ width: '18%' }}>How often</th>
                <th style={{ width: '28%' }}>When to take</th>
              </tr>
            </thead>
            <tbody>
              {consultation.medicines.map((m, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>
                    <div className="rx-med-name">
                      {m.name} {m.type ? `(${m.type})` : ''}
                    </div>
                    {m.instruction && <div className="rx-med-sub">{m.instruction}</div>}
                  </td>
                  <td>
                    {m.frequency}
                    {m.dose && <div className="rx-med-sub">{m.dose}</div>}
                  </td>
                  <td>{m.timing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {consultation.testsAdvised?.length > 0 && (
        <div className="rx-section rx-tests-section">
          <div className="rx-section-title">Tests Advised</div>
          {/* Each test with the clinic it should be done at, so the patient can
              walk in with this sheet. */}
          <table className="rx-med-table">
            <thead>
              <tr>
                <th style={{ width: '6%' }}>No.</th>
                <th style={{ width: '32%' }}>Test</th>
                <th style={{ width: '24%' }}>Get it done at</th>
                <th style={{ width: '16%' }}>Phone</th>
                <th style={{ width: '22%' }}>Address</th>
              </tr>
            </thead>
            <tbody>
              {consultation.testsAdvised.map((t, i) => {
                // Older prescriptions kept one clinic for the whole sheet.
                const clinic = t.referralClinic || consultation.referralClinic;
                return (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="rx-med-name">{t.name || t}</td>
                    <td className="rx-med-name">{clinic?.name || '-'}</td>
                    <td>{clinic?.phone || '-'}</td>
                    <td>{clinic?.address || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Eats the blank middle of the sheet, so what follows settles just above
          the signature instead of floating under the tables. */}
      <div className="rx-spacer" />

      {/* When to come back, sitting right above the advice. */}
      {consultation.followUpDate && (
        <div className="rx-section rx-followup-section">
          <span className="rx-followup">
            {formatDate(consultation.followUpDate)} ના રોજ ફરી બતાવવા આવવું અને તકલીફ જણાય તો વચ્ચે આવવું.
          </span>
        </div>
      )}

      {consultation.precautions?.length > 0 && (
        <div className="rx-section">
          <div className="rx-section-title"> Advice</div>
          <div className="rx-advice">
            {consultation.precautions.map((p, i) => (
              <div key={i}>{p}</div>
            ))}
          </div>
        </div>
      )}

      {consultation.advice && (
        <div className="rx-section">
          <div className="rx-section-title">General Advice</div>
          <div>{consultation.advice}</div>
        </div>
      )}

      <div className="rx-footer">
        <div className="rx-note">{clinic.footerNote}</div>
        <div className="rx-sign">
          {doctorName}
          <div style={{ fontSize: '8pt', color: '#555' }}>{doctorQualification}</div>
        </div>
      </div>
    </div>
  );
});

export default PrescriptionSheet;
